import { useRef, useEffect } from "react";
import { X } from "lucide-react";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  className?: string;
  children: React.ReactNode;
  showCloseButton?: boolean; // Default to true
  isFullscreen?: boolean; // Default to false for backwards compatibility
  title?: string; // Optional title to display at the top
  headerContent?: React.ReactNode; // For fully custom headers
  headerActions?: React.ReactNode; // Action buttons in header (Download, etc)
  maxWidth?: string; // Optional max width (defaults to 600px)

  // Footer actions
  footerActions?: React.ReactNode; // Custom footer actions
  primaryAction?: {
    label: string;
    onClick: () => void;
    disabled?: boolean;
    loading?: boolean;
    variant?: "primary" | "danger";
  };
  secondaryAction?: {
    label: string;
    onClick?: () => void; // Defaults to onClose if not provided
    disabled?: boolean;
  };
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  children,
  className,
  showCloseButton = true,
  isFullscreen = false,
  title,
  headerContent,
  headerActions,
  maxWidth = "600px",
  footerActions,
  primaryAction,
  secondaryAction,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const contentClasses = isFullscreen
    ? "w-full h-full bg-white dark:bg-gray-900 overflow-y-auto"
    : "relative w-full rounded-3xl bg-white dark:bg-gray-900";

  return (
    <div className="fixed inset-0 flex items-center justify-center modal z-99999 m-0">
      {!isFullscreen && (
        <div
          className="fixed inset-0 h-full w-full bg-gray-400/50 backdrop-blur-[32px]"
          onClick={onClose}
        ></div>
      )}

      <div
        ref={modalRef}
        className={`${contentClasses} ${className || ""}`}
        onClick={(e) => e.stopPropagation()}
        style={!isFullscreen ? { maxWidth } : undefined}
      >
        {/* Header with inline close button */}
        <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 px-6 py-4">
          {/* Title or custom header content */}
          {headerContent ? (
            <div className="flex-1">{headerContent}</div>
          ) : title ? (
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {title}
            </h3>
          ) : (
            <div></div>
          )}

          {/* Optional action buttons */}
          {headerActions && (
            <div className="flex items-center gap-2 ml-4">{headerActions}</div>
          )}

          {/* Inline close button */}
          {showCloseButton && (
            <button
              onClick={onClose}
              className="ml-2 p-1 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Content */}
        <div className={isFullscreen ? "p-4" : "p-6"}>{children}</div>

        {/* Footer Actions */}
        {(footerActions || primaryAction || secondaryAction) && (
          <div className="flex items-center justify-end gap-3 border-t border-gray-200 dark:border-gray-700 px-6 py-4">
            {footerActions ? (
              footerActions
            ) : (
              <>
                {secondaryAction && (
                  <button
                    type="button"
                    onClick={secondaryAction.onClick || onClose}
                    disabled={secondaryAction.disabled}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {secondaryAction.label}
                  </button>
                )}
                {primaryAction && (
                  <button
                    type="button"
                    onClick={primaryAction.onClick}
                    disabled={primaryAction.disabled || primaryAction.loading}
                    className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                      primaryAction.variant === "danger"
                        ? "bg-red-600 hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600"
                        : "bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
                    }`}
                  >
                    {primaryAction.loading ? "Loading..." : primaryAction.label}
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
